import React, { useMemo, useState } from 'react';
import { Card, Button, Modal, Form, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { 
  useGetCurrentSeasonQuery, 
  useGetLeagueSeasonsQuery, 
  useCreateNextSeasonMutation, 
  useEndSeasonMutation 
} from '../slices/seasonsApiSlice';
import { useGetDraftByLeagueQuery } from '../slices/draftApiSlice';
import { trackSeasonCreated, trackEvent } from '../utils/analytics';
import Icon from './Icon.jsx';
import { CalendarDays, Settings, Flag, PlusCircle, CalendarClock } from 'lucide-react';

const toDateTimeLocalValue = (dateValue) => {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
};

const getMinimumSeasonStartValue = (draftStartTime) => {
  const baseDate = draftStartTime ? new Date(draftStartTime) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return toDateTimeLocalValue(new Date());
  }

  baseDate.setMinutes(baseDate.getMinutes() + 1);
  const now = new Date();

  return toDateTimeLocalValue(baseDate > now ? baseDate : now);
};

const getFutureScheduledDraftState = ({ draftStartTime, leagueDraft }) => {
  const rawDraftStartTime = draftStartTime || leagueDraft?.startTime || null;
  if (!rawDraftStartTime) {
    return {
      resolvedDraftStartTime: null,
      hasUpcomingDraftDate: false,
    };
  }

  const parsedDraftStartTime = new Date(rawDraftStartTime);
  const hasUpcomingDraftDate = Boolean(
    !Number.isNaN(parsedDraftStartTime.getTime())
    && parsedDraftStartTime > new Date()
    && (draftStartTime ? true : leagueDraft?.status === 'scheduled')
  );

  return {
    resolvedDraftStartTime: rawDraftStartTime,
    hasUpcomingDraftDate,
  };
};

const SeasonManagement = ({ leagueId, isAdmin = false, draftStartTime = null }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSeasonStartDate, setNewSeasonStartDate] = useState('');
  const { data: leagueDraft } = useGetDraftByLeagueQuery(leagueId, {
    skip: !leagueId || Boolean(draftStartTime),
  });
  const { resolvedDraftStartTime, hasUpcomingDraftDate } = getFutureScheduledDraftState({
    draftStartTime,
    leagueDraft,
  });
  const minimumSeasonStartValue = useMemo(
    () => getMinimumSeasonStartValue(resolvedDraftStartTime),
    [resolvedDraftStartTime]
  );

  const {
    data: currentSeason,
    error: currentSeasonError,
    isLoading: currentSeasonLoading,
    refetch: refetchCurrentSeason
  } = useGetCurrentSeasonQuery(leagueId, {
    skip: !leagueId
  });

  const {
    data: allSeasons,
    error: allSeasonsError,
    isLoading: allSeasonsLoading,
    refetch: refetchAllSeasons
  } = useGetLeagueSeasonsQuery(leagueId, {
    skip: !leagueId
  });

  const [createNextSeason, { isLoading: isCreating }] = useCreateNextSeasonMutation();
  const [endSeason, { isLoading: isEnding }] = useEndSeasonMutation();

  const handleCreateNextSeason = async (e) => {
    e.preventDefault();
    try {
      if (!hasUpcomingDraftDate) {
        toast.error('Set a new future draft date before creating the next season.');
        return;
      }

      if (newSeasonStartDate && new Date(newSeasonStartDate) <= new Date(resolvedDraftStartTime)) {
        toast.error('Season start date must be after the draft date.');
        return;
      }

      const result = await createNextSeason({
        leagueId,
        startDate: newSeasonStartDate
      }).unwrap();
      
      // Track season creation
      trackSeasonCreated(result.id || 'unknown', leagueId);
      
      toast.success('Next season created successfully!');
      setShowCreateModal(false);
      setNewSeasonStartDate('');
      refetchCurrentSeason();
      refetchAllSeasons();
    } catch (error) {
      console.error('Failed to create next season:', error);
      toast.error(error?.data?.message || 'Failed to create next season');
    }
  };

  const handleEndSeason = async (seasonId) => {
    if (!window.confirm('Are you sure you want to end this season? This will calculate final standings and cannot be undone.')) {
      return;
    }

    try {
      await endSeason(seasonId).unwrap();
      
      // Track season ending
      trackEvent('season_ended', {
        season_id: seasonId,
        league_id: leagueId,
        event_category: 'admin',
      });
      
      toast.success('Season ended successfully! Final standings have been calculated.');
      refetchCurrentSeason();
      refetchAllSeasons();
    } catch (error) {
      console.error('Failed to end season:', error);
      toast.error(error?.data?.message || 'Failed to end season');
    }
  };

  if (currentSeasonLoading || allSeasonsLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading season data...</p>
      </div>
    );
  }

  const errorMessage = currentSeasonError?.data?.message || allSeasonsError?.data?.message || '';
  const upcomingSeason = allSeasons?.seasons?.find((season) => season.status === 'upcoming');

  if ((currentSeasonError || allSeasonsError) && errorMessage && !errorMessage.includes('No active season found')) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Season Data</Alert.Heading>
        <p>{errorMessage}</p>
      </Alert>
    );
  }

  return (
    <div>
      {errorMessage.includes('No active season found') && upcomingSeason && (
        <Alert variant="info" className="mb-4">
          <Alert.Heading><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> Season Coming Soon</Alert.Heading>
          <p className="mb-0">
            The next season starts on <strong>{new Date(upcomingSeason.startDate).toLocaleDateString()}</strong>.
            Team details and leaderboards will be available when the season begins.
          </p>
        </Alert>
      )}

      {errorMessage.includes('No active season found') && !upcomingSeason && (
        <Alert variant="info" className="mb-4">
          <Alert.Heading><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> No Active Season</Alert.Heading>
          <p className="mb-0">
            There is currently no active season for this league.
            {isAdmin ? ' Create one after the draft has been scheduled.' : ' Check back soon for the next season announcement.'}
          </p>
        </Alert>
      )}

      {isAdmin && !hasUpcomingDraftDate && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> Draft Date Required</Alert.Heading>
          <p className="mb-2">
            Set a new future draft date before creating the next season.
          </p>
          <p className="mb-0">
            Creating the next season clears the live team rosters so the league can run a fresh draft while keeping prior season results intact.
          </p>
        </Alert>
      )}

      {/* Current Season Management */}
      {isAdmin && (
          <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0"><Icon icon={Settings} size={16} className="me-2 align-middle" /> Season Management</h5>
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {currentSeason?.season ? (
                  <>
                    <h6 className="mb-1">Current Season: {currentSeason.season.seasonNumber}</h6>
                    <small className="text-muted">
                      {new Date(currentSeason.season.startDate).toLocaleDateString()} - {new Date(currentSeason.season.endDate).toLocaleDateString()}
                    </small>
                  </>
                ) : upcomingSeason ? (
                  <>
                    <h6 className="mb-1">Upcoming Season: {upcomingSeason.seasonNumber}</h6>
                    <small className="text-muted">
                      {new Date(upcomingSeason.startDate).toLocaleDateString()} - {new Date(upcomingSeason.endDate).toLocaleDateString()}
                    </small>
                  </>
                ) : (
                  <>
                    <h6 className="mb-1">No season scheduled yet</h6>
                    <small className="text-muted">Set a season start date after the draft date.</small>
                  </>
                )}
              </div>
              <div>
                {currentSeason?.season?.status === 'active' && (
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleEndSeason(currentSeason.season.id)}
                    disabled={isEnding}
                    className="me-2"
                  >
                    {isEnding ? <Spinner size="sm" className="me-1" /> : <Icon icon={Flag} size={16} className="me-1" />} End Season
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setNewSeasonStartDate(minimumSeasonStartValue);
                    setShowCreateModal(true);
                  }}
                  disabled={isCreating || !hasUpcomingDraftDate || Boolean(upcomingSeason)}
                >
                  <Icon icon={PlusCircle} size={16} className="me-1" /> Create Next Season
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Season History */}
      {allSeasons?.seasons && allSeasons.seasons.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0"><Icon icon={CalendarClock} size={16} className="me-2 align-middle" /> Season History</h5>
            <small className="text-muted">{allSeasons.totalSeasons} total seasons</small>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Season</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Champions</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {allSeasons.seasons.map((season) => (
                  <tr key={season.id}>
                    <td>
                      <strong>Season {season.seasonNumber}</strong>
                    </td>
                    <td>
                      <small>
                        {new Date(season.startDate).toLocaleDateString()} - {new Date(season.endDate).toLocaleDateString()}
                      </small>
                    </td>
                    <td>
                      <Badge 
                        bg={season.status === 'active' ? 'success' : 
                            season.status === 'upcoming' ? 'warning' : 'secondary'}
                        className="text-capitalize"
                      >
                        {season.status}
                      </Badge>
                    </td>
                    <td>
                      {season.status === 'completed' && season.champions ? (
                        <div className="small">
                          {season.champions.first && (
                            <div>🥇 {season.champions.first.name}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">TBD</span>
                      )}
                    </td>
                    <td>
                      <span className="small">{season.progress}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Create Next Season Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Next Season</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateNextSeason}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Season Start Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={newSeasonStartDate}
                onChange={(e) => setNewSeasonStartDate(e.target.value)}
                min={minimumSeasonStartValue}
                required
              />
              <Form.Text className="text-muted">
                The season must start after the draft date and will last exactly 3 months from this date.
              </Form.Text>
            </Form.Group>
            {resolvedDraftStartTime && (
              <Alert variant="info" className="mb-0">
                Draft date: <strong>{new Date(resolvedDraftStartTime).toLocaleString()}</strong>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={isCreating}
            >
              {isCreating ? <Spinner size="sm" className="me-1" /> : ''}
              Create Season
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SeasonManagement;
